import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { colors } from '../theme';
import { inputStyle, labelStyle, Field } from '../formPrimitives';
import { sanitizeName } from '../nameUtils';
import { listDepartments, createDepartment } from '../api';
import type { Department } from '../types';

const NEW_DEPARTMENT_VALUE = '__new__';

/**
 * "Which team owns this?" — shared by every create flow (CreateFlowPage,
 * CreateAiAgentPage) since a newly-created repo's name is prefixed with the
 * selected department's slug (`sre-idp-<department>-<app>`, see
 * repoNameForApplication in github-activities.ts) and every deploy tags the
 * Coolify app with MUMZWORLD_DEPARTMENT either way.
 *
 * The department list itself isn't fixed — "+ Add new department" reveals an
 * inline name/slug form that POSTs a new one and selects it immediately, no
 * page reload.
 */
export function DepartmentSelect({
  value,
  onChange,
  id = 'department',
  label = 'Department',
}: {
  value: string;
  onChange: (slug: string) => void;
  id?: string;
  /** The visible label. The select keeps aria-label "Department" regardless. */
  label?: string;
}) {
  const identityApi = useApi(identityApiRef);
  const [departments, setDepartments] = useState<Department[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listDepartments(identityApi)
      .then((list) => {
        if (cancelled) return;
        setDepartments(list);
        // Default to the first department only when nothing is selected yet
        // — never overrides a value the parent already set (e.g. re-mount).
        if (!value && list[0]) onChange(list[0].slug);
      })
      .catch((err) => {
        if (!cancelled) setLoadError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startAdding = () => {
    setNewName('');
    setNewSlug('');
    setSlugTouched(false);
    setAddError(null);
    setAdding(true);
  };

  const submitNewDepartment = async () => {
    const name = newName.trim();
    if (!name) return;
    setSubmitting(true);
    setAddError(null);
    try {
      const created = await createDepartment(identityApi, name, newSlug.trim() || undefined);
      setDepartments((prev) => [...(prev ?? []), created]);
      onChange(created.slug);
      setAdding(false);
    } catch (err) {
      setAddError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Field>
      <label style={labelStyle} htmlFor={id}>
        {label}
      </label>

      {loadError && (
        <div role="alert" style={{ fontSize: 12, color: colors.danger, marginBottom: 6 }}>
          Couldn&apos;t load departments: {loadError}
        </div>
      )}

      {!adding && (
        <select
          id={id}
          style={inputStyle}
          value={departments ? value : ''}
          onChange={(e) => {
            if (e.target.value === NEW_DEPARTMENT_VALUE) {
              startAdding();
              return;
            }
            onChange(e.target.value);
          }}
          aria-label="Department"
          disabled={!departments}
        >
          {!departments && <option value="">Loading…</option>}
          {departments?.map((dept) => (
            <option key={dept.slug} value={dept.slug}>
              {dept.name}
            </option>
          ))}
          <option value={NEW_DEPARTMENT_VALUE}>+ Add new department…</option>
        </select>
      )}

      {adding && (
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12 }}>
          <label style={{ ...labelStyle, fontSize: 12 }} htmlFor={`${id}-new-name`}>
            Department name
          </label>
          <input
            id={`${id}-new-name`}
            style={inputStyle}
            value={newName}
            onChange={(e) => {
              const nextName = e.target.value;
              setNewName(nextName);
              if (!slugTouched) setNewSlug(sanitizeName(nextName));
            }}
            placeholder="e.g. Logistics"
            aria-label="New department name"
          />
          <div style={{ marginTop: 10 }}>
            <label style={{ ...labelStyle, fontSize: 12 }} htmlFor={`${id}-new-slug`}>
              Slug (used in repo names)
            </label>
            <input
              id={`${id}-new-slug`}
              style={inputStyle}
              value={newSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setNewSlug(sanitizeName(e.target.value));
              }}
              placeholder="e.g. logistics"
              aria-label="New department slug"
            />
          </div>
          {addError && (
            <div role="alert" style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>
              {addError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              onClick={submitNewDepartment}
              disabled={submitting || !newName.trim()}
              style={{
                padding: '7px 14px',
                borderRadius: 8,
                border: 'none',
                background: submitting || !newName.trim() ? colors.borderLight : colors.primary,
                color: submitting || !newName.trim() ? colors.textMuted : '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: submitting || !newName.trim() ? 'default' : 'pointer',
              }}
            >
              {submitting ? 'Adding…' : 'Add department'}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              disabled={submitting}
              style={{
                padding: '7px 14px',
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                background: colors.surface,
                color: colors.text,
                fontSize: 13,
                fontWeight: 600,
                cursor: submitting ? 'default' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Field>
  );
}
